import { enumerable } from '@Common/Core/CoreDecorator';
import { Scroll } from '@View/Core/Scroll';
import { GridColumn } from './GridColumn';
import { GridRender } from './GridRender';
import { PagerControl } from './PagerControl';
import { ColumnsFitType, ColumnWidth, ColumnWidthType, IGridPager, IGridProperties } from './Settings';
import { Grid } from './SimpleGridLight';

export class SimpleGridLayout {
    public static SizingLogging = false;
    public scrolledToEnd: () => void;
    public pageChange: (page: number) => void;
    public bodyScroller: Scroll;
    public headerScroller: Scroll;
    public pager: PagerControl;
    private readonly grid: Grid;
    private readonly gridRender: GridRender;
    private readonly gridProperties: IGridProperties;
    private isAdjusted = false;
    private commonWidth = 10;
    private commonHeight = 10;
    private needRecalcContentWidth: boolean;
    private syncScroll: Scroll;
    private storeCalcContentWidth: number[];
    private readonly paddingRight = Scroll.getScrollSize().verticalSize;

    constructor(grid: Grid, contex: GridRender, gridProperties: IGridProperties, pagerSetting: IGridPager) {
        this.grid = grid;
        this.gridRender = contex;
        if (gridProperties.hideHeader) {
            this.gridRender.headerTable.parent().hide();
            this.gridRender.root.addClass('simpleGrid-header');
        }
        this.gridProperties = gridProperties;
        if (pagerSetting.pageSize) {
            this.pager = new PagerControl({
                itemsCount: this.grid.visibilityRows().length,
                pageSize: pagerSetting.pageSize,
                distance: 2,
                startPage: pagerSetting.startPage,
                pageChange: page => {
                    this.pageChange(page);
                }
            });
            this.pager.getControl().appendTo(contex.root);
        }

        this.bodyScroller = new Scroll(this.gridRender.bodyContainer, {
            scroller: this.gridRender.bodyScrollableArea,
            rightPadding: false,
            onScrolledToEnd: () => {
                this.scrolledToEnd();
                this.bodyScroller.refresh();
            },
            onScrollStart: () => {
                this.syncScroll = this.headerScroller;
            },
            onScrollMove: this.syncScrollBarHandler.bind(this)
        });

        this.headerScroller = new Scroll(this.gridRender.headerContainer, {
            scroller: this.gridRender.headerScrollableArea,
            scrollY: false,
            scrollbars: false,
            onScrollStart: () => {
                this.syncScroll = this.bodyScroller;
            },
            onScrollMove: this.syncScrollBarHandler.bind(this)
        });

        this.needRecalcContentWidth = true;

        if (pagerSetting.pageSize) {
            this.pager = new PagerControl({
                itemsCount: this.grid.visibilityRows().length,
                pageSize: pagerSetting.pageSize,
                distance: 2,
                startPage: pagerSetting.startPage,
                pageChange: page => {
                    this.pageChange(page);
                    this.bodyScroller.refresh();
                }
            });
            this.pager.getControl().prependTo(contex.root);
        }
    }

    @enumerable
    private get sizingLogging(): boolean {
        return SimpleGridLayout.SizingLogging || this.gridProperties['sizingLogging'];
    }

    public doLayout(): void {
        this.grid.columnsCollection.sizingLogging = this.sizingLogging;

        const minGridWidth = _.sum(this.getGridColumns().map(gc => gc.minWidth));
        this.commonWidth = Math.max(this.gridRender.root.innerWidth() ?? 0, minGridWidth);
        this.commonHeight = this.gridRender.root.innerHeight() ?? 0;

        if (!this.isAdjusted) {
            this.adjustWidth(this.commonWidth - this.paddingRight);
            this.isAdjusted = true;
        }
        this.adjustHeight(this.commonHeight);
        this.refreshScroll();
    }

    public refreshScroll() {
        this.gridRender.bodyContainer.css({ paddingRight: this.paddingRight });
        this.gridRender.headerContainer.css({ paddingRight: this.paddingRight });

        if (this.bodyScroller) {
            this.bodyScroller.refresh(null, true);
        }
        if (this.headerScroller) {
            this.headerScroller.refresh(null, true);
        }

        if (this.pager) {
            this.pager.itemsCount(this.grid.visibilityRows().length);
            this.pager.refresh();
        }
    }

    public reset() {
        this.isAdjusted = false;
    }

    public destroy() {
        this.bodyScroller.destroy();
        this.headerScroller.destroy();
    }

    private syncScrollBarHandler(scroll: Scroll, delta: { deltaX: number; deltaY: number }): void {
        if (!this.syncScroll || scroll === this.syncScroll) {
            return;
        }
        this.syncScroll.setScrollPosition({
            x: scroll.getScrollPosition().x,
            y: this.syncScroll.getScrollPosition().y
        });
    }

    private adjustHeight(commonHeight: number): void {
        commonHeight -= this.gridRender.headerTable.height();
        if (!this.gridProperties.footer.flexible) {
            commonHeight -= this.gridRender.footer.height();
        }
        if (this.pager) {
            commonHeight -= this.pager.getControl().height();
        }
        this.gridRender.bodyContainer.height(commonHeight);
    }

    private adjustWidth(commonWidth: number) {
        const widthPreDef = [...this.initColumnsWithDesired(), ...this.initColumnsByContents()];
        const widthStar = this.initColumnsWithStar();

        const countStar = widthStar.length;
        const countPreDef = widthPreDef.length;

        const avgWidth = commonWidth / (countStar + countPreDef);

        for (const cw of widthStar) {
            cw.calcValue = correctLimits(cw, avgWidth);
        }

        if (this.gridProperties.columnsFit === ColumnsFitType.Fit) {
            this.fitWidthLayout(commonWidth, widthPreDef, widthStar);
        } else {
            this.growWidthLayout(commonWidth, false);
        }

        return this.updateWidth();
    }

    private growWidthLayout(commonWidth: number, bidirection = true) {
        const allColumns = this.getGridColumns().map(c => c.getWidthDef());

        let rest = commonWidth - _.sumBy(allColumns, cw => (cw.calcValue = Math.floor(cw.calcValue)));

        if (bidirection || rest > 0) {
            for (let i = 0; i < allColumns.length && rest !== 0; i++) {
                const cw = allColumns[i];
                const floor = Math.floor(rest / (allColumns.length - i));
                const grow = Math.min(cw.max - cw.calcValue, floor);
                rest -= grow;
                cw.calcValue += grow;
            }
        }
    }

    private fitWidthLayout(commonWidth: number, widthPreDef: ColumnWidth[], widthStar: ColumnWidth[]) {
        let tryCount = 0;
        let sumPreDef = _.sumBy(widthPreDef, cw => cw.calcValue || cw.min);
        let sumStar = _.sumBy(widthStar, cw => cw.calcValue || cw.min);
        let tryingWidthStar: number;
        let tryingWidthPreDef = Math.floor(commonWidth * (sumPreDef / (sumPreDef + sumStar)));

        this.loggingSizing('start', commonWidth);
        do {
            tryingWidthStar = commonWidth - tryResizeWidth(widthPreDef, tryingWidthPreDef - sumPreDef);
            tryingWidthPreDef = commonWidth - tryResizeWidth(widthStar, tryingWidthStar - sumStar);
            sumPreDef = _.sumBy(widthPreDef, cw => cw.calcValue);
            sumStar = _.sumBy(widthStar, cw => cw.calcValue);
        } while (tryCount++ < 1 && sumPreDef + sumStar !== commonWidth);

        this.growWidthLayout(commonWidth);
        this.loggingSizing('finish', commonWidth);
    }

    private loggingSizing(point: string, width: number): void {
        if (this.sizingLogging) {
            const values = this.getGridColumns().map(c => c.getWidthDef().calcValue);
            console.log.apply(console, [point, width, '-', _.sum(values), '=', width - _.sum(values), ' - >', '[', ...values, ']']);
        }
    }

    private getGridColumns(): GridColumn[] {
        const columns = this.grid.columns();
        for (const column of columns) {
            checkColumnWidth(column);
        }
        return columns;
    }

    private initColumnsWithDesired(): ColumnWidth[] {
        const desiredColumns = this.getGridColumns()
            .filter(gc => !!gc.getWidthDef().desired)
            .map(gc => gc.getWidthDef());
        for (const cw of desiredColumns) {
            cw.calcValue = cw.desired;
        }
        return desiredColumns;
    }

    private initColumnsByContents(): ColumnWidth[] {
        const allContentColumn = this.getColumnsByType(ColumnWidthType.byTitleContent);
        if (this.needRecalcContentWidth) {
            this.needRecalcContentWidth = !this.gridRender.isVisible();

            if (this.needRecalcContentWidth) {
                return allContentColumn.map(col => col.getWidthDef());
            }

            for (const col of allContentColumn) {
                col.getWidthDef().calcValue = 0;
            }

            const byTitle = this.getColumnsByType(ColumnWidthType.byTitle);
            const byContent = this.getColumnsByType(ColumnWidthType.byContent);

            byTitle.length && this.calcHeaderColumnWidth(byTitle);
            byContent.length && this.calcBodyColumnWidth(byContent);

            this.storeCalcContentWidth = allContentColumn.map(col => col.getWidthDef().calcValue);
        } else {
            for (let i = 0; i < allContentColumn.length; i++) {
                const cw = allContentColumn[i].getWidthDef();
                cw.calcValue = this.storeCalcContentWidth[i];
            }
        }
        return allContentColumn.map(col => col.getWidthDef());
    }

    private initColumnsWithStar(): ColumnWidth[] {
        return this.getGridColumns()
            .map(gc => gc.getWidthDef())
            .filter(cw => cw.type === ColumnWidthType.star)
            .filter(cw => cw.desired === void 0);
    }

    private calcHeaderColumnWidth(columns: GridColumn[]) {
        const row = $('tr:first', this.gridRender.headerTable);
        const clone = row.clone();
        clone.appendTo(row.parent());
        const headerCells = $('th', clone);
        clone.css('position', 'absolute');
        headerCells.each((cellIndex, cell) => {
            $(cell).width('auto');
        });
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            const cw = column.getWidthDef();
            cw.calcValue = $(headerCells[column.columnIndex()]).outerWidth() ?? 0;
        }
        clone.remove();
    }

    private calcBodyColumnWidth(columns: GridColumn[]) {
        const filter = this.grid.rows().filter(row => row.isRendered);
        this.gridRender.bodyTable.toggleClass('resetWidth', true);
        for (let rowIndex = 0; rowIndex < filter.length; rowIndex++) {
            const row = filter[rowIndex];
            for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                const column = columns[columnIndex];
                const widthDef = column.getWidthDef();
                widthDef.max = widthDef.calcValue = Math.max(widthDef.calcValue, row.getCell(column).tableCell().outerWidth() ?? 0);
            }
        }
        this.gridRender.bodyTable.toggleClass('resetWidth', false);
    }

    private getColumnsByType(type: ColumnWidthType): GridColumn[] {
        return this.getGridColumns().filter(gc => (gc.getWidthDef().type & type) > 0 && !gc.getWidthDef().desired);
    }

    private updateWidth(): number {
        let sumWidth = 0;

        for (const gc of this.grid.columns()) {
            sumWidth += gc.getWidthDef().calcValue;
            gc.width(gc.getWidthDef().calcValue);
        }

        return sumWidth;
    }
}

function tryResizeWidth(widths: ColumnWidth[], diff: number): number {
    let count = widths.length;
    return _.sumBy(widths, cw => {
        const columnDiff = diff / count--;
        const preWidth = cw.calcValue;
        cw.calcValue = correctLimits(cw, columnDiff + preWidth);
        diff -= cw.calcValue - preWidth;
        return cw.calcValue;
    });
}

function correctLimits(cw: ColumnWidth, value: number): number {
    return correctValueLimits(cw.min, cw.max, value);
}

function correctValueLimits(min: number, max: number, value: number): number {
    return Math.min(Math.max(value, min), max);
}

function checkColumnWidth(gc: GridColumn): void {
    const widthDef = gc.getWidthDef();
    widthDef.min = Math.max(gc.minWidth, widthDef.min || 0);
    widthDef.max = Math.max(widthDef.max || Number.MAX_VALUE, widthDef.min);
    if (widthDef.desired) {
        widthDef.desired = correctLimits(widthDef, widthDef.desired);
    }
}
